/**
 * ID Scanner Parser Utility
 * Supports: USA DL, Canada DL, Mexico ID, Passports
 * Methods: PDF417 barcode, MRZ (passports), mag stripe, manual entry
 */

/**
 * Parse scanned ID data and extract guest information
 * @param {string} rawData - Raw scanned data from ID scanner
 * @returns {object} Parsed guest data or null if parsing fails
 */
export function parseScannedId(rawData) {
  if (!rawData || typeof rawData !== 'string') {
    return null;
  }

  const data = rawData.trim();
  
  console.log('[ID Scanner] Attempting to parse:', data.substring(0, 100));
  
  // Try AAMVA PDF417 format (US/Canada Driver's License barcode)
  const aamvaResult = parseAAMVA(data);
  if (aamvaResult) {
    console.log('[ID Scanner] Parsed as AAMVA (US/Canada DL)');
    return aamvaResult;
  }
  
  // Try Mexico INE/IFE format (Mexican National ID)
  const mexicoResult = parseMexicoINE(data);
  if (mexicoResult) {
    console.log('[ID Scanner] Parsed as Mexico INE/IFE');
    return mexicoResult;
  }
  
  // Try MRZ format (Passport/ID Card - all countries)
  const mrzResult = parseMRZ(data);
  if (mrzResult) {
    console.log('[ID Scanner] Parsed as MRZ (Passport)');
    return mrzResult;
  }
  
  // Try Mag Stripe format (swipe readers)
  const magStripeResult = parseMagStripe(data);
  if (magStripeResult) {
    console.log('[ID Scanner] Parsed as Mag Stripe');
    return magStripeResult;
  }
  
  // Try simple key-value format
  const kvResult = parseKeyValue(data);

  // Try XML/JSON format (Thales QS2000 and professional scanners)
  const xmlJsonResult = parseXMLJSON(data);
  if (xmlJsonResult) {
    console.log('[ID Scanner] Parsed as XML/JSON (Thales QS2000 or similar)');
    return xmlJsonResult;
  }

  if (kvResult) {
    console.log('[ID Scanner] Parsed as Key-Value format');
    return kvResult;
  }
  
  console.error('[ID Scanner] Failed to parse - no matching format');
  return null;
}

/**
 * Parse AAMVA PDF417 format (Driver's License)
 * Supports: USA (all states), Canada (all provinces)
 * Format: @\nANSI ... DL ... with field codes
 * Versions: AAMVA 2000-2024 standards
 */
function parseAAMVA(data) {
  // More comprehensive AAMVA detection
  // Look for ANSI header, DL subfile, or known field codes
  if (!data.includes('ANSI') && 
      !data.includes('DL') && 
      !data.includes('DAC') && 
      !data.includes('DCS') &&
      !data.includes('DAQ')) {
    return null;
  }

  console.log('[ID Scanner] Detected AAMVA format, parsing...');

  const result = {
    firstName: '',
    lastName: '',
    middleName: '',
    documentNumber: '',
    dateOfBirth: '',
    addressLine1: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'USA',
    documentType: 'DRIVERS_LICENSE'
  };

  // AAMVA field codes - comprehensive mapping
  const fieldMap = {
    'DAC': 'firstName',       // First name / Given name
    'DCT': 'firstName',       // First name (alternate)
    'DCS': 'lastName',        // Last name / Family name / Surname
    'DAD': 'middleName',      // Middle name
    'DBB': 'dateOfBirth',     // Date of birth
    'DAQ': 'documentNumber',  // Document discriminator / DL number
    'DAA': 'documentNumber',  // License/ID number (alternate)
    'DCF': 'documentNumber',  // Document discriminator (newer versions)
    'DAG': 'addressLine1',    // Street address 1
    'DAH': 'addressLine2',    // Street address 2
    'DAI': 'city',            // City
    'DAJ': 'state',           // State/Province
    'DAK': 'postalCode',      // Postal code / ZIP
    'DCG': 'country',         // Country (USA, CAN, MEX)
    'DBA': 'expirationDate',  // Expiration date
    'DBD': 'issueDate',       // Issue date
    'DBC': 'gender',          // Sex/Gender
    'DAY': 'eyeColor',        // Eye color
    'DAU': 'height',           // Height
    'DAZ': 'hairColor',       // Hair color
    'DCU': 'eyeColor',        // Eye color
    'DCE': 'weightRange',     // Weight range
    'DAW': 'weight',          // Weight in lbs
    'DDE': 'familyNameTruncated', // Family name truncated flag
    'DDF': 'givenNameTruncated',  // Given name truncated flag
    'DDG': 'middleNameTruncated'  // Middle name truncated flag
  };

  // Strategy 1: Parse line by line (newline separated)
  const lines = data.split(/[\r\n]+/);
  for (const line of lines) {
    const trimmedLine = line.trim();
    if (trimmedLine.length < 3) continue;
    
    for (const [code, field] of Object.entries(fieldMap)) {
      if (trimmedLine.startsWith(code)) {
        let value = trimmedLine.substring(3).trim();
        
        // Format dates (MMDDYYYY -> YYYY-MM-DD)
        if ((code === 'DBB' || code === 'DBA' || code === 'DBD') && value.length >= 8) {
          value = formatAAMVADate(value.substring(0, 8));
        }
        
        // Trim postal code to 5 digits
        if (code === 'DAK' && value.length > 5) {
          value = value.substring(0, 5);
        }
        
        if (!result[field] || result[field] === '') {
          result[field] = value;
        }
      }
    }
  }
  
  // Strategy 2: Regex-based extraction (handles concatenated fields, always runs for missing fields)
  for (const [code, field] of Object.entries(fieldMap)) {
    if (result[field] && result[field] !== '') continue;
    
    const regex = new RegExp(code + '([^\\r\\n\\x00-\\x1F]*?)(?=D[A-Z][A-Z0-9]|Z[A-Z]|\\r|\\n|\\x00|$)', 'g');
    const match = regex.exec(data);
    
    if (match && match[1]) {
      let value = match[1].trim();
      
      if ((code === 'DBB' || code === 'DBA' || code === 'DBD') && value.length >= 8) {
        value = formatAAMVADate(value.substring(0, 8));
      }
      
      if (code === 'DAK' && value.length > 5) {
        value = value.substring(0, 5);
      }
      
      if (value) {
        result[field] = value;
      }
    }
  }

  // Detect Canadian provinces
  const canadianProvinces = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];
  if (result.state && canadianProvinces.includes(result.state.toUpperCase())) {
    result.country = 'Canada';
  }

  console.log('[ID Scanner] AAMVA parsed result:', {
    firstName: result.firstName,
    lastName: result.lastName,
    documentNumber: result.documentNumber,
    state: result.state
  });

  // Return null if no critical fields found
  if (!result.firstName && !result.lastName && !result.documentNumber) {
    console.log('[ID Scanner] AAMVA parsing failed - no critical fields found');
    return null;
  }

  return result;
}

/**
 * Format AAMVA date from MMDDYYYY or YYYYMMDD to YYYY-MM-DD
 */
function formatAAMVADate(dateStr) {
  if (dateStr.length !== 8) return dateStr;
  
  let year, month, day;
  
  // Detect format by checking first 4 digits
  const first4 = parseInt(dateStr.substring(0, 4));
  
  if (first4 > 1900 && first4 < 2100) {
    // YYYYMMDD format
    year = dateStr.substring(0, 4);
    month = dateStr.substring(4, 6);
    day = dateStr.substring(6, 8);
  } else {
    // MMDDYYYY format
    month = dateStr.substring(0, 2);
    day = dateStr.substring(2, 4);
    year = dateStr.substring(4, 8);
  }
  
  return `${year}-${month}-${day}`;
}


/**
 * Parse Mexico INE/IFE format (Mexican National ID)
 * Format varies but typically includes CURP or matricula
 */
function parseMexicoINE(data) {
  // Check for Mexican ID indicators
  const hasCURP = data.match(/[A-Z]{4}\d{6}[HM][A-Z]{5}[0-9A-Z]\d/); // CURP format
  const hasINE = data.includes('INE') || data.includes('IFE') || data.includes('MEXICO');
  
  if (!hasCURP && !hasINE) {
    return null;
  }

  const result = {
    firstName: '',
    lastName: '',
    documentNumber: '',
    dateOfBirth: '',
    country: 'Mexico'
  };

  // Extract CURP if present (18-character Mexican ID)
  if (hasCURP) {
    result.documentNumber = hasCURP[0];
    
    // CURP encodes DOB: positions 4-9 are YYMMDD
    const curp = hasCURP[0];
    const yy = curp.substring(4, 6);
    const mm = curp.substring(6, 8);
    const dd = curp.substring(8, 10);
    const year = parseInt(yy) > 30 ? `19${yy}` : `20${yy}`;
    result.dateOfBirth = `${year}-${mm}-${dd}`;
  }

  // Try to extract name from key-value pairs
  const lines = data.split(/[\r\n]+/);
  for (const line of lines) {
    if (line.includes('NOMBRE') || line.includes('NAME')) {
      const parts = line.split(/[=:]/);
      if (parts.length >= 2) result.firstName = parts[1].trim();
    }
    if (line.includes('APELLIDO') || line.includes('SURNAME')) {
      const parts = line.split(/[=:]/);
      if (parts.length >= 2) result.lastName = parts[1].trim();
    }
  }

  if (!result.documentNumber) {
    return null;
  }

  return result;
}

/**
 * Parse Magnetic Stripe format (Track 1 & 2)
 * Used by swipe card readers
 * Supports: Texas DL format and standard mag stripe formats
 * Example: %TXSAN ANTONIO^PATEL$OM^1123 BRANCH SPG^?;63601539740516=311019991011?
 */
function parseMagStripe(data) {
  const result = {
    firstName: '',
    lastName: '',
    documentNumber: '',
    dateOfBirth: '',
    addressLine1: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'USA'
  };
  
  // State-specific DL Mag Stripe Formats
  // Format: %STATE_CODECITY^LASTNAME$FIRSTNAME^ADDRESS^?;...
  // Supports: TX (Texas), CA (California), FL (Florida), NY (New York), etc.
  const stateDLMatch = data.match(/%([A-Z]{2})([^^]+)\^([^$]+)\$([^^]+)\^([^^]+)\^/);
  if (stateDLMatch) {
    const [_, stateCode, city, lastName, firstName, address] = stateDLMatch;
    
    result.state = stateCode.trim();
    result.city = city.trim();
    result.lastName = lastName.trim();
    result.firstName = firstName.trim();
    result.addressLine1 = address.trim();
    result.country = 'USA';
    
    // Extract ID number from Track 2: ;636XXXXXXXXXXX where ID is last 8 digits before =
    // 636 is the USA country code in AAMVA standard
    const idMatch = data.match(/;636(\d+)=/);
    if (idMatch) {
      const fullNumber = idMatch[1];
      // Take last 8 digits as the ID number (works for most states)
      result.documentNumber = fullNumber.substring(fullNumber.length - 8);
    }
    
    // Extract DOB: After = sign, last 8 digits are typically YYYYMMDD
    const dobMatch = data.match(/=(\d+)\?/);
    if (dobMatch) {
      const track2Data = dobMatch[1];
      if (track2Data.length >= 8) {
        const dobStr = track2Data.substring(track2Data.length - 8);
        const year = dobStr.substring(0, 4);
        const month = dobStr.substring(4, 6);
        const day = dobStr.substring(6, 8);
        result.dateOfBirth = `${year}-${month}-${day}`;
      }
    }
    
    // Extract ZIP code: Common patterns +00XXXXX or +XXXXX
    const zipMatch = data.match(/\+0{0,2}(\d{5})/);
    if (zipMatch) {
      result.postalCode = zipMatch[1];
    }
    
    result.documentType = 'DRIVERS_LICENSE';
    
    console.log(`[ID Scanner] Parsed ${stateCode} DL mag stripe`);
    return result;
  }
  
  // Standard Track 1 format: %B{PAN}^{Name}^{Additional Data}?
  const track1Match = data.match(/%B(\d+)\^([^?]+)\^([^?]+)\?/);
  if (track1Match) {
    const [_, pan, name, additional] = track1Match;
    const nameParts = name.split('/').map(p => p.trim());
    
    result.firstName = nameParts[1] || '';
    result.lastName = nameParts[0] || '';
    result.documentNumber = pan;
    result.documentType = 'DRIVERS_LICENSE';
    
    console.log('[ID Scanner] Parsed standard Track 1 mag stripe');
    return result;
  }

  // Track 2 format (less info): ;{PAN}={Expiry}{Service Code}?
  const track2Match = data.match(/;(\d+)=(\d{4})/);
  if (track2Match) {
    const [_, pan, expiry] = track2Match;
    result.documentNumber = pan;
    result.documentType = 'DRIVERS_LICENSE';
    
    console.log('[ID Scanner] Parsed Track 2 mag stripe');
    return result;
  }
  
  return null;
}
    


/**
 * Parse XML/JSON format from professional document readers
 * Supports: Thales Gemalto QS2000, Regula, ARH, and similar devices
 */
function parseXMLJSON(data) {
  try {
    // Try parsing as JSON first (most modern scanners)
    if (data.trim().startsWith('{') || data.trim().startsWith('[')) {
      const jsonData = JSON.parse(data);
      
      // Thales QS2000 JSON format
      if (jsonData.DocumentData || jsonData.document || jsonData.AAMVA || jsonData.MRZ) {
        return parseThalesJSON(jsonData);
      }
      
      // Generic JSON format
      return parseGenericJSON(jsonData);
    }
    
    // Try parsing as XML (older scanners or specific configurations)
    if (data.trim().startsWith('<')) {
      return parseXML(data);
    }
  } catch (error) {
    console.log('[ID Scanner] Not valid JSON/XML format');
  }
  
  return null;
}

/**
 * Parse Thales QS2000 JSON output
 */
function parseThalesJSON(jsonData) {
  const result = {
    firstName: '',
    lastName: '',
    middleName: '',
    documentNumber: '',
    dateOfBirth: '',
    addressLine1: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  };

  // Check AAMVA data (Driver's License)
  const aamvaData = jsonData.AAMVA || jsonData.DocumentData?.AAMVA;
  if (aamvaData) {
    result.firstName = aamvaData.FirstName || aamvaData.DAC || '';
    result.lastName = aamvaData.LastName || aamvaData.DCS || '';
    result.middleName = aamvaData.MiddleName || aamvaData.DAD || '';
    result.documentNumber = aamvaData.LicenseNumber || aamvaData.DAQ || '';
    result.dateOfBirth = aamvaData.DateOfBirth || aamvaData.DBB || '';
    result.addressLine1 = aamvaData.Address || aamvaData.DAG || '';
    result.city = aamvaData.City || aamvaData.DAI || '';
    result.state = aamvaData.State || aamvaData.DAJ || '';
    result.postalCode = aamvaData.ZipCode || aamvaData.DAK || '';
    result.country = aamvaData.Country || 'USA';
  }

  // Check MRZ data (Passport)
  const mrzData = jsonData.MRZ || jsonData.DocumentData?.MRZ;
  if (mrzData) {
    result.firstName = mrzData.GivenNames || mrzData.FirstName || '';
    result.lastName = mrzData.Surname || mrzData.LastName || '';
    result.documentNumber = mrzData.DocumentNumber || mrzData.PassportNumber || '';
    result.dateOfBirth = mrzData.DateOfBirth || '';
    result.country = mrzData.Nationality || mrzData.IssuingCountry || '';
  }

  // Generic fields
  if (jsonData.PersonalData) {
    result.firstName = result.firstName || jsonData.PersonalData.FirstName || '';
    result.lastName = result.lastName || jsonData.PersonalData.LastName || '';
    result.dateOfBirth = result.dateOfBirth || jsonData.PersonalData.BirthDate || '';
  }

  // Format DOB if needed
  if (result.dateOfBirth && result.dateOfBirth.length === 8 && /^\d+$/.test(result.dateOfBirth)) {
    // Convert YYYYMMDD to YYYY-MM-DD
    result.dateOfBirth = `${result.dateOfBirth.substring(0,4)}-${result.dateOfBirth.substring(4,6)}-${result.dateOfBirth.substring(6,8)}`;
  }

  if (!result.firstName && !result.lastName && !result.documentNumber) {
    return null;
  }

  return result;
}

/**
 * Parse generic JSON from various scanners
 */
function parseGenericJSON(jsonData) {
  const result = {
    firstName: jsonData.firstName || jsonData.first_name || jsonData.givenName || '',
    lastName: jsonData.lastName || jsonData.last_name || jsonData.surname || '',
    middleName: jsonData.middleName || jsonData.middle_name || '',
    documentNumber: jsonData.documentNumber || jsonData.idNumber || jsonData.licenseNumber || '',
    dateOfBirth: jsonData.dateOfBirth || jsonData.dob || jsonData.birthDate || '',
    addressLine1: jsonData.address || jsonData.street || '',
    city: jsonData.city || '',
    state: jsonData.state || jsonData.province || '',
    postalCode: jsonData.zipCode || jsonData.postalCode || '',
    country: jsonData.country || jsonData.nationality || ''
  };

  if (!result.firstName && !result.lastName && !result.documentNumber) {
    return null;
  }

  return result;
}

/**
 * Parse XML format from document readers
 */
function parseXML(xmlData) {
  const result = {
    firstName: '',
    lastName: '',
    documentNumber: '',
    dateOfBirth: '',
    country: ''
  };

  // Simple XML tag extraction (basic parser)
  const extractTag = (tag) => {
    const match = xmlData.match(new RegExp(`<${tag}>([^<]+)</${tag}>`, 'i'));
    return match ? match[1].trim() : '';
  };

  result.firstName = extractTag('FirstName') || extractTag('GivenName') || extractTag('DAC');
  result.lastName = extractTag('LastName') || extractTag('Surname') || extractTag('DCS');
  result.middleName = extractTag('MiddleName') || extractTag('DAD');
  result.documentNumber = extractTag('DocumentNumber') || extractTag('LicenseNumber') || extractTag('DAQ');
  result.dateOfBirth = extractTag('DateOfBirth') || extractTag('BirthDate') || extractTag('DBB');
  result.country = extractTag('Country') || extractTag('Nationality') || extractTag('IssuingCountry');

  if (!result.firstName && !result.lastName && !result.documentNumber) {
    return null;
  }

  return result;
}

/**
 * Parse MRZ format (Machine Readable Zone from Passport)
 * Format: Multiple lines starting with P< or I<
 */
function parseMRZ(data) {
  // Check for MRZ pattern
  if (!data.match(/^[PI]</m)) {
    return null;
  }

  const result = {
    firstName: '',
    lastName: '',
    documentNumber: '',
    dateOfBirth: '',
    country: ''
  };

  const lines = data.split(/[\r\n]+/).filter(line => line.trim());
  
  if (lines.length < 2) return null;

  // Line 1: Document type, country, names
  // Format: P<USADOE<<JOHN<<<<<<<<<<<<<<<<<<<<<<<<<<<<
  const line1 = lines[0];
  if (line1.length > 5) {
    const countryCode = line1.substring(2, 5);
    result.country = countryCode;
    
    // Names are after country code, separated by <<
    const namesSection = line1.substring(5).replace(/</g, ' ').trim();
    const nameParts = namesSection.split(/\s+/);
    if (nameParts.length >= 1) result.lastName = nameParts[0];
    if (nameParts.length >= 2) result.firstName = nameParts[1];
  }

  // Line 2: Document number, DOB, etc.
  // Format: L898902C<3UTO6908061F9406236ZE184226B<<<<<14
  const line2 = lines[1];
  if (line2.length > 9) {
    result.documentNumber = line2.substring(0, 9).replace(/</g, '');
    
    // DOB is typically at position 13-19 (YYMMDD format)
    if (line2.length > 19) {
      const dobStr = line2.substring(13, 19);
      if (dobStr.length === 6 && /^\d+$/.test(dobStr)) {
        const year = parseInt(dobStr.substring(0, 2));
        const month = dobStr.substring(2, 4);
        const day = dobStr.substring(4, 6);
        const fullYear = year > 30 ? `19${year}` : `20${year}`;
        result.dateOfBirth = `${fullYear}-${month}-${day}`;
      }
    }
  }

  if (!result.documentNumber && !result.firstName) {
    return null;
  }

  return result;
}

/**
 * Parse simple key-value format
 * Format: FIRSTNAME=John\nLASTNAME=Doe\nDOCNUMBER=123456
 */
function parseKeyValue(data) {
  const lines = data.split(/[\r\n]+/);
  const kvPairs = {};
  
  for (const line of lines) {
    const parts = line.split(/[=:]/);
    if (parts.length === 2) {
      const key = parts[0].trim().toUpperCase();
      const value = parts[1].trim();
      kvPairs[key] = value;
    }
  }

  if (Object.keys(kvPairs).length === 0) {
    return null;
  }

  const result = {
    firstName: kvPairs['FIRSTNAME'] || kvPairs['FIRST_NAME'] || kvPairs['GIVENNAME'] || '',
    lastName: kvPairs['LASTNAME'] || kvPairs['LAST_NAME'] || kvPairs['SURNAME'] || '',
    documentNumber: kvPairs['DOCNUMBER'] || kvPairs['DOC_NUMBER'] || kvPairs['IDNUMBER'] || kvPairs['ID_NUMBER'] || '',
    dateOfBirth: kvPairs['DOB'] || kvPairs['DATEOFBIRTH'] || kvPairs['BIRTHDATE'] || '',
    addressLine1: kvPairs['ADDRESS'] || kvPairs['STREET'] || '',
    city: kvPairs['CITY'] || '',
    state: kvPairs['STATE'] || kvPairs['PROVINCE'] || '',
    postalCode: kvPairs['ZIP'] || kvPairs['ZIPCODE'] || kvPairs['POSTAL'] || kvPairs['POSTALCODE'] || '',
    country: kvPairs['COUNTRY'] || ''
  };

  if (!result.firstName && !result.lastName && !result.documentNumber) {
    return null;
  }

  return result;
}

/**
 * Validate and normalize parsed data
 */
export function normalizeGuestData(parsedData) {
  if (!parsedData) return null;

  return {
    firstName: parsedData.firstName?.trim() || '',
    lastName: parsedData.lastName?.trim() || '',
    middleName: parsedData.middleName?.trim() || '',
    fullName: [parsedData.firstName, parsedData.middleName, parsedData.lastName]
      .filter(Boolean)
      .join(' ')
      .trim(),
    documentNumber: parsedData.documentNumber?.trim().toUpperCase() || '',
    dateOfBirth: parsedData.dateOfBirth || '',
    addressLine1: parsedData.addressLine1?.trim() || '',
    city: parsedData.city?.trim() || '',
    state: parsedData.state?.trim() || '',
    postalCode: parsedData.postalCode?.trim() || '',
    country: parsedData.country?.trim() || ''
  };
}
